import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Key, Copy, RefreshCw, Trash2, Plus, Shield } from "lucide-react";

export default function ApiKeysPanel({ backendUrl, getAuthConfig }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ client_id: "", label: "", email: "", daily_limit: 100, burst_limit: 10 });
  const [copiedKey, setCopiedKey] = useState(false);

  const fetchKeys = useCallback(async () => {
    try {
      const { data } = await axios.get(`${backendUrl}/api/admin/api-keys`, getAuthConfig());
      setKeys(data.keys || []);
    } catch {}
    setLoading(false);
  }, [backendUrl, getAuthConfig]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    if (!form.client_id || !form.label) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${backendUrl}/api/admin/api-keys`, form, getAuthConfig());
      setNewKey(data);
      fetchKeys();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to create key");
    }
    setCreating(false);
  };

  const handleRevoke = async (id) => {
    if (!window.confirm("Revoke this API key?")) return;
    try {
      await axios.delete(`${backendUrl}/api/admin/api-keys/${id}`, getAuthConfig());
      fetchKeys();
    } catch {}
  };

  const handleRotate = async (id) => {
    if (!window.confirm("Rotate this API key? The old key will stop working.")) return;
    try {
      const { data } = await axios.post(`${backendUrl}/api/admin/api-keys/${id}/rotate`, {}, getAuthConfig());
      setNewKey(data);
      fetchKeys();
    } catch {}
  };

  const copyKey = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 3000);
  };

  if (loading) return <p className="body-text">Loading API keys...</p>;

  return (
    <div>
      {/* New Key Alert */}
      {newKey?.api_key && (
        <div data-testid="new-key-alert" style={{
          background: "rgba(142,217,115,0.08)", border: "1px solid rgba(142,217,115,0.3)",
          borderRadius: 8, padding: 16, marginBottom: 24
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <Shield size={16} style={{ color: "#8ED973" }} />
            <span className="body-text" style={{ fontWeight: 600, color: "#8ED973" }}>New API Key Created</span>
          </div>
          <p className="muted-caption" style={{ marginBottom: 8 }}>
            Copy this key now. It will not be shown again.
          </p>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, background: "rgba(0,0,0,0.3)",
            borderRadius: 6, padding: "8px 12px", fontFamily: "monospace", fontSize: 13
          }}>
            <code data-testid="new-key-value" style={{ flex: 1, wordBreak: "break-all", color: "#e0e0e0" }}>
              {newKey.api_key}
            </code>
            <button
              data-testid="copy-key-btn"
              onClick={() => copyKey(newKey.api_key)}
              style={{ background: "none", border: "none", cursor: "pointer", color: copiedKey ? "#8ED973" : "#888", padding: 4 }}
            >
              <Copy size={14} />
            </button>
          </div>
          {copiedKey && <p style={{ color: "#8ED973", fontSize: 11, marginTop: 4 }}>Copied!</p>}
          <button
            onClick={() => setNewKey(null)}
            className="ghost-link" style={{ marginTop: 8, fontSize: 11 }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Create Form */}
      <div className="admin-card" style={{ marginBottom: 24 }}>
        <h3 className="body-text" style={{ fontWeight: 600, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <Plus size={14} /> Generate API Key
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label className="muted-caption" style={{ display: "block", marginBottom: 4, fontSize: 11 }}>Client ID</label>
            <input
              data-testid="apikey-client-id"
              className="admin-input"
              placeholder="e.g. org_acme"
              value={form.client_id}
              onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
            />
          </div>
          <div>
            <label className="muted-caption" style={{ display: "block", marginBottom: 4, fontSize: 11 }}>Label</label>
            <input
              data-testid="apikey-label"
              className="admin-input"
              placeholder="e.g. prod-research"
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label className="muted-caption" style={{ display: "block", marginBottom: 4, fontSize: 11 }}>Client Email (optional — sends key + integration guide)</label>
            <input
              data-testid="apikey-email"
              className="admin-input"
              type="email"
              placeholder="client@company.com"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            />
          </div>
          <div>
            <label className="muted-caption" style={{ display: "block", marginBottom: 4, fontSize: 11 }}>Daily Limit</label>
            <input
              data-testid="apikey-daily-limit"
              className="admin-input"
              type="number"
              placeholder="100"
              value={form.daily_limit}
              onChange={e => setForm(f => ({ ...f, daily_limit: parseInt(e.target.value) || 100 }))}
            />
          </div>
          <div>
            <label className="muted-caption" style={{ display: "block", marginBottom: 4, fontSize: 11 }}>Burst Limit (per min)</label>
            <input
              data-testid="apikey-burst-limit"
              className="admin-input"
              type="number"
              placeholder="10"
              value={form.burst_limit}
              onChange={e => setForm(f => ({ ...f, burst_limit: parseInt(e.target.value) || 10 }))}
            />
          </div>
        </div>
        <button
          data-testid="create-apikey-btn"
          className="cta-pill cta-pill-filled"
          style={{ marginTop: 12, padding: "10px 28px", fontSize: "0.82rem", cursor: "pointer" }}
          onClick={handleCreate}
          disabled={creating || !form.client_id || !form.label}
        >
          {creating ? "Creating..." : "Generate Key"}
        </button>
      </div>

      {/* Keys Table */}
      <div className="admin-card" style={{ overflowX: "auto" }}>
        <h3 className="body-text" style={{ fontWeight: 600, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <Key size={14} /> Active API Keys ({keys.length})
        </h3>
        {keys.length === 0 ? (
          <p className="muted-caption">No API keys yet.</p>
        ) : (
          <table className="admin-table" data-testid="apikeys-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Label</th>
                <th>Prefix</th>
                <th>Status</th>
                <th>Today</th>
                <th>Total</th>
                <th>Last Used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.id} style={{ opacity: k.status === "revoked" ? 0.5 : 1 }}>
                  <td style={{ fontWeight: 500 }}>{k.client_id}</td>
                  <td>{k.label}</td>
                  <td style={{ fontFamily: "monospace", fontSize: 11 }}>{k.key_prefix}...</td>
                  <td>
                    <span style={{
                      padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                      background: k.status === "active" ? "rgba(142,217,115,0.15)" : "rgba(232,124,124,0.15)",
                      color: k.status === "active" ? "#8ED973" : "#e87c7c",
                    }}>
                      {k.status}
                    </span>
                  </td>
                  <td>{k.daily_used}/{k.daily_limit}</td>
                  <td>{k.total_calls}</td>
                  <td className="muted-caption" style={{ fontSize: 11 }}>
                    {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : "Never"}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      {k.status === "active" && (
                        <>
                          <button
                            data-testid={`rotate-key-${k.id}`}
                            onClick={() => handleRotate(k.id)}
                            title="Rotate key"
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: 2 }}
                          >
                            <RefreshCw size={13} />
                          </button>
                          <button
                            data-testid={`revoke-key-${k.id}`}
                            onClick={() => handleRevoke(k.id)}
                            title="Revoke key"
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#e87c7c", padding: 2 }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
